import { outputTimeFormat } from 'constant/formats';
import type { Dayjs } from 'dayjs';
import { dayjs } from 'services/datesAndTimes';

import { stripSecureMessageHtml } from 'utils/helpers/secureMessageHtml';

import {
  IUser,
  SecureMessageArchiveFlag,
  SecureMessageConversationItem,
  SecureMessageListItem,
  SecureMessageListStatus
} from 'types';

export const SECURE_MESSAGE_SELF_LABEL = 'Me';
export const SECURE_MESSAGE_SYSTEM_LABEL = 'System Message';

export const SECURE_MESSAGE_BORDER_COLORS = {
  provider: '#5F5F5F',
  client: '#0378E3',
  system: '#3ea24a'
} as const;

export const isSecureMessageSystemMessage = (
  isSystemMsg?: boolean | number | null
): boolean => isSystemMsg === true || isSystemMsg === 1;

export const isSecureMessageRead = (
  isRead?: SecureMessageListItem['is_read'] | boolean | string | null
): boolean => {
  if (isRead === true || isRead === 1) {
    return true;
  }

  if (typeof isRead === 'string') {
    const normalized = isRead.trim().toLowerCase();

    return (
      normalized === '1' ||
      normalized === 'true' ||
      normalized === 'y' ||
      normalized === 'yes'
    );
  }

  return Number(isRead) === 1;
};

export const isSecureMessageFromCurrentUser = (
  message: Pick<
    SecureMessageConversationItem,
    'msg_direction' | 'sender_id' | 'recipient_id'
  >,
  currentUserId?: number | null
): boolean => {
  if (currentUserId != null && message.sender_id === currentUserId) {
    return true;
  }

  if (
    currentUserId != null &&
    message.recipient_id === currentUserId &&
    message.sender_id !== currentUserId
  ) {
    return false;
  }

  if (currentUserId != null && message.sender_id > 0) {
    return false;
  }

  return message.msg_direction === 'out';
};

export const getSecureMessageBorderColor = (
  message: Pick<SecureMessageConversationItem, 'is_system_msg'> &
    Pick<
      SecureMessageConversationItem,
      'msg_direction' | 'sender_id' | 'recipient_id'
    >,
  currentUserId?: number | null
): string => {
  if (isSecureMessageSystemMessage(message.is_system_msg)) {
    return SECURE_MESSAGE_BORDER_COLORS.system;
  }

  if (isSecureMessageFromCurrentUser(message, currentUserId)) {
    return SECURE_MESSAGE_BORDER_COLORS.client;
  }

  return SECURE_MESSAGE_BORDER_COLORS.provider;
};

export const isSecureMessageReplySuccessful = (
  response?: { success?: boolean | number | null } | null
): boolean => response?.success === true || response?.success === 1;

/** Prefers the API's own `message` over a generic fallback for failed mutations. */
export const resolveSecureMessageErrorMessage = (
  response: unknown,
  fallback: string
): string => {
  if (typeof response === 'object' && response !== null) {
    const { message } = response as { message?: unknown };

    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }

  return fallback;
};

export const resolveSecureMessageReplyMsgId = (
  responseMsgId: number,
  existingMessages: SecureMessageConversationItem[]
): number => {
  if (!existingMessages.some((message) => message.msg_id === responseMsgId)) {
    return responseMsgId;
  }

  return -Date.now();
};

export const getCurrentUserDisplayName = (user: IUser | null): string =>
  [user?.givname, user?.surname].filter(Boolean).join(' ') ||
  user?.nickname?.trim() ||
  user?.username?.trim() ||
  '';

export const getCurrentUserPhotoUrl = (
  user: IUser | null,
  previewPhoto?: string | null,
  fallback?: string
): string =>
  previewPhoto?.trim() ||
  user?.cl_photo?.trim() ||
  user?.photo?.trim() ||
  fallback?.trim() ||
  '';

export const getSecureMessageSenderLabel = (
  message: Pick<
    SecureMessageConversationItem,
    | 'msg_direction'
    | 'sender_id'
    | 'recipient_id'
    | 'sender_name'
    | 'is_system_msg'
  >,
  currentUserId?: number | null
): string => {
  if (isSecureMessageSystemMessage(message.is_system_msg)) {
    return SECURE_MESSAGE_SYSTEM_LABEL;
  }

  if (isSecureMessageFromCurrentUser(message, currentUserId)) {
    return SECURE_MESSAGE_SELF_LABEL;
  }

  return message.sender_name?.trim() || 'Unknown sender';
};

export const buildSecureMessageReplyItem = (
  responseMsgId: number,
  msg_text: string,
  user: IUser | null,
  template?: SecureMessageConversationItem
): SecureMessageConversationItem => ({
  msg_id: responseMsgId,
  sender_type: template?.sender_type ?? '',
  sender_id: user?.id ?? 0,
  sender_name:
    [user?.givname, user?.surname].filter(Boolean).join(' ') ||
    user?.nickname ||
    '',
  sender_descr: '',
  sender_photo_url: user?.cl_photo || user?.photo || '',
  recipient_type: template?.recipient_type ?? '',
  recipient_id: template?.recipient_id ?? 0,
  recipient_name: template?.recipient_name ?? '',
  recipient_photo_url: template?.recipient_photo_url ?? '',
  msg_subject: template?.msg_subject ?? '',
  msg_text,
  msg_ts: dayjs().format('MM/DD/YYYY hh:mm A'),
  msg_direction: 'out',
  is_system_msg: false,
  message_closed: false
});

export const getSecureMessageListPartyLabel = (
  item: Pick<SecureMessageListItem, 'snd_id' | 'snd_name' | 'is_system_msg'>,
  currentUserId?: number | null
): string => {
  if (isSecureMessageSystemMessage(item.is_system_msg)) {
    return SECURE_MESSAGE_SYSTEM_LABEL;
  }

  if (currentUserId != null && item.snd_id === currentUserId) {
    return SECURE_MESSAGE_SELF_LABEL;
  }

  return item.snd_name;
};

const SECURE_MESSAGE_TIME_INPUT_FORMATS = [
  'HH:mm:ss',
  'HH:mm',
  'h:mm A',
  'h:mm a',
  'hh:mm A',
  'hh:mm a',
  'h:mmA',
  'h:mma',
  'hh:mmA',
  'hh:mma'
] as const;

const formatSecureMessageTime = (value: Dayjs): string =>
  value.format(outputTimeFormat);

const normalizeSecureMessageTimeFallback = (time: string): string =>
  time.replace(/\b(am|pm)\b/gi, (match) => match.toUpperCase());

const parseSecureMessageTime = (time: string): Dayjs | null => {
  const trimmed = time.trim();

  if (!trimmed) {
    return null;
  }

  const matchedFormat = SECURE_MESSAGE_TIME_INPUT_FORMATS.find((format) =>
    dayjs(trimmed, format, true).isValid()
  );

  if (!matchedFormat) {
    return null;
  }

  return dayjs(trimmed, matchedFormat, true);
};

export const getSecureMessageListSubject = (
  item: SecureMessageListItem
): string => {
  const subjectRaw = item.subject || item.msg_subject || '';

  return stripSecureMessageHtml(subjectRaw);
};

const normalizeSecureMessageListPreview = (raw: string): string =>
  stripSecureMessageHtml(raw).replace(/\s+/g, ' ').trim();

export const getSecureMessageListPreview = (
  item: SecureMessageListItem
): string => {
  const textPreview = normalizeSecureMessageListPreview(item.text || '');
  const msgTextPreview = normalizeSecureMessageListPreview(item.msg_text || '');

  return textPreview.length >= msgTextPreview.length
    ? textPreview
    : msgTextPreview;
};

/** Raw body from the list API (`text` / `msg_text`) for inline expand — not stripped. */
export const getSecureMessageListBodyContent = (
  item: SecureMessageListItem
): string => {
  const textBody = item.text?.trim() ?? '';
  const msgTextBody = item.msg_text?.trim() ?? '';

  if (!textBody) {
    return msgTextBody;
  }

  if (!msgTextBody) {
    return textBody;
  }

  const textPreview = normalizeSecureMessageListPreview(textBody);
  const msgTextPreview = normalizeSecureMessageListPreview(msgTextBody);

  return textPreview.length >= msgTextPreview.length ? textBody : msgTextBody;
};

export const getSecureMessageListFirstSentence = (
  item: SecureMessageListItem
): string => {
  const preview = getSecureMessageListPreview(item);

  if (!preview) {
    return '';
  }

  const sentenceMatch = preview.match(/^[\s\S]*?[.!?](?:\s|$)/);

  if (sentenceMatch) {
    return sentenceMatch[0].trim();
  }

  return preview;
};

export const hasSecureMessageAdditionalBodyText = (
  item: SecureMessageListItem
): boolean => {
  const preview = getSecureMessageListPreview(item);
  const firstSentence = getSecureMessageListFirstSentence(item);

  if (!preview || !firstSentence) {
    return false;
  }

  const normalizedPreview = preview.trim();
  const normalizedFirstSentence = firstSentence.trim();

  return normalizedPreview.length > normalizedFirstSentence.length;
};

export const getSecureMessageListFirstSentenceDisplay = (
  item: SecureMessageListItem
): string => {
  const firstSentence = getSecureMessageListFirstSentence(item);

  if (!firstSentence) {
    return '';
  }

  if (hasSecureMessageAdditionalBodyText(item)) {
    return `${firstSentence}…`;
  }

  return firstSentence;
};

/** Stable thread key for list deduplication, routing, and conversation/reply APIs. */
export const getSecureMessageThreadId = (
  item: Pick<SecureMessageListItem, 'msg_id' | 'thread_id'>
): number => item.thread_id ?? item.msg_id;

/**
 * Row ids the thread was built from. archive-sec-message flags a single row, so a
 * thread with both a sent and a received row needs both ids to change state as a whole.
 */
export const getSecureMessageRowIds = (
  item: Pick<SecureMessageListItem, 'msg_id' | 'msgIds'>
): number[] => (item.msgIds?.length ? item.msgIds : [item.msg_id]);

export const isSecureMessageArchived = (
  item: Pick<SecureMessageListItem, 'is_archive'>
): boolean => Number(item.is_archive) === 1;

export const getSecureMessageListTimestampValue = (
  item: Pick<SecureMessageListItem, 'snd_date' | 'snd_time' | 'msg_id'>
): number => {
  if (item.snd_date) {
    const combined = `${item.snd_date} ${item.snd_time || ''}`.trim();
    const parsed = dayjs(combined, 'YYYY-MM-DD HH:mm:ss', true);

    if (parsed.isValid()) {
      return parsed.valueOf();
    }

    const fallback = dayjs(combined);

    if (fallback.isValid()) {
      return fallback.valueOf();
    }
  }

  return item.msg_id;
};

const toReadFlag = (value: unknown): number =>
  isSecureMessageRead(
    value as SecureMessageListItem['is_read'] | boolean | string | null
  )
    ? 1
    : 0;

const mergeSecureMessageListItems = (
  existing: SecureMessageListItem,
  incoming: SecureMessageListItem
): SecureMessageListItem => {
  const threadId = getSecureMessageThreadId(existing);
  const existingTimestamp = getSecureMessageListTimestampValue(existing);
  const incomingTimestamp = getSecureMessageListTimestampValue(incoming);
  const latest = incomingTimestamp >= existingTimestamp ? incoming : existing;
  const preservedSubject =
    getSecureMessageListSubject(existing) ||
    getSecureMessageListSubject(incoming);

  return {
    ...latest,
    msg_id: threadId,
    thread_id: threadId,
    msgIds: Array.from(
      new Set([
        ...getSecureMessageRowIds(existing),
        ...getSecureMessageRowIds(incoming)
      ])
    ),
    subject: preservedSubject,
    msg_subject:
      existing.msg_subject ||
      incoming.msg_subject ||
      preservedSubject ||
      latest.msg_subject
  };
};

/** Thread id becomes the row key, so the row's own id is kept in `msgIds`. */
const normalizeSecureMessageThreadRow = (
  item: SecureMessageListItem
): SecureMessageListItem => {
  const threadId = getSecureMessageThreadId(item);

  return {
    ...item,
    msg_id: threadId,
    thread_id: threadId,
    msgIds: getSecureMessageRowIds(item)
  };
};

/**
 * One row per thread; when sent/received overlap, keep the latest activity.
 * Read state is per conversation and comes only from the received list —
 * sent rows often have is_read=0 and must not control subject weight.
 * Threads that only exist on the sent list are treated as read.
 */
export const mergeSecureMessagesLists = (
  lists: SecureMessageListItem[][]
): SecureMessageListItem[] => {
  const [sent = [], received = []] = lists;
  const byThread = new Map<number, SecureMessageListItem>();
  const receivedReadByThread = new Map<number, number>();

  const collect = (item: SecureMessageListItem) => {
    const threadId = getSecureMessageThreadId(item);
    const normalized = normalizeSecureMessageThreadRow(item);
    const existing = byThread.get(threadId);

    byThread.set(
      threadId,
      existing ? mergeSecureMessageListItems(existing, normalized) : normalized
    );
  };

  received.forEach((item) => {
    receivedReadByThread.set(
      getSecureMessageThreadId(item),
      toReadFlag(item.is_read)
    );
    collect(item);
  });

  sent.forEach(collect);

  return Array.from(byThread.values())
    .map((item) => {
      const threadId = getSecureMessageThreadId(item);
      const receivedIsRead = receivedReadByThread.get(threadId);

      return {
        ...item,
        is_read:
          receivedIsRead !== undefined
            ? receivedIsRead
            : 1 /* sent-only thread */
      };
    })
    .sort(
      (left, right) =>
        getSecureMessageListTimestampValue(right) -
        getSecureMessageListTimestampValue(left)
    );
};

/** get-sec-messages filters by row, so the list status maps straight to `is_archive`. */
export const getSecureMessageArchiveFlag = (
  status: SecureMessageListStatus
): SecureMessageArchiveFlag =>
  status === SecureMessageListStatus.Archived ? 1 : 0;

export const getSecureMessageConversationSubject = (
  messages: SecureMessageConversationItem[]
): string => {
  if (!messages.length) {
    return '';
  }

  const messageWithSubject = messages.find((message) =>
    stripSecureMessageHtml(message.msg_subject || '')
  );

  if (messageWithSubject) {
    return stripSecureMessageHtml(messageWithSubject.msg_subject || '');
  }

  const firstWithBody = messages.find((message) => message.msg_text?.trim());

  return stripSecureMessageHtml(firstWithBody?.msg_text || '').slice(0, 160);
};

export const SECURE_MESSAGE_PRIORITY_BACKGROUND = {
  important: '#FFF4B7',
  urgent: '#FFE0E0'
} as const;

export const getSecureMessagePriorityBackground = (
  priority: number,
  defaultBackground?: string
): string | undefined => {
  if (priority === 2) {
    return SECURE_MESSAGE_PRIORITY_BACKGROUND.urgent;
  }

  if (priority === 1) {
    return SECURE_MESSAGE_PRIORITY_BACKGROUND.important;
  }

  return defaultBackground;
};

export const getSecureMessagePriorityLabel = (priority: number): string => {
  if (priority === 2) {
    return 'Urgent';
  }

  if (priority === 1) {
    return 'Important';
  }

  return 'Normal';
};

const formatSecureMessageShortDate = (parsed: Dayjs): string => {
  const today = dayjs();

  if (parsed.isSame(today, 'day')) {
    return 'Today';
  }

  if (parsed.isSame(today, 'year')) {
    return parsed.format('ddd, MMM D');
  }

  /* The weekday is dropped once a year is shown, otherwise the date outgrows its column. */
  return parsed.format('MMM D, YYYY');
};

export const formatSecureMessageListDate = (date: string): string => {
  if (!date) {
    return '';
  }

  const parsed = dayjs(date, 'YYYY-MM-DD', true);

  if (!parsed.isValid()) {
    const fallback = dayjs(date);

    if (!fallback.isValid()) {
      return date;
    }

    return formatSecureMessageShortDate(fallback);
  }

  return formatSecureMessageShortDate(parsed);
};

export const formatSecureMessageDashboardTime = (time: string): string => {
  if (!time) {
    return '';
  }

  const parsed = parseSecureMessageTime(time);

  if (parsed) {
    return formatSecureMessageTime(parsed);
  }

  return normalizeSecureMessageTimeFallback(time.trim());
};

const parseSecureMessageTimestamp = (timestamp: string): Dayjs | null => {
  const trimmed = timestamp.trim();

  if (!trimmed) {
    return null;
  }

  const normalized = trimmed.replace(/(\d)(AM|PM)$/i, '$1 $2');

  const slashDateParsed = dayjs(normalized, 'MM/DD/YYYY hh:mm A', true);

  if (slashDateParsed.isValid()) {
    return slashDateParsed;
  }

  const isoDateParsed = dayjs(normalized, 'YYYY-MM-DD HH:mm:ss', true);

  if (isoDateParsed.isValid()) {
    return isoDateParsed;
  }

  const fallback = dayjs(normalized);

  if (fallback.isValid()) {
    return fallback;
  }

  return null;
};

export const getSecureMessageTimestampValue = (
  message: SecureMessageConversationItem
): number => {
  const parsed = parseSecureMessageTimestamp(message.msg_ts);

  if (parsed) {
    return parsed.valueOf();
  }

  return message.msg_id;
};

export const sortSecureMessageConversation = (
  messages: SecureMessageConversationItem[]
): SecureMessageConversationItem[] =>
  [...messages].sort((left, right) => {
    const leftTimestamp = getSecureMessageTimestampValue(left);
    const rightTimestamp = getSecureMessageTimestampValue(right);

    if (leftTimestamp !== rightTimestamp) {
      return leftTimestamp - rightTimestamp;
    }

    return left.msg_id - right.msg_id;
  });

export const formatConversationTimestamp = (timestamp: string): string => {
  if (!timestamp) {
    return '';
  }

  const parsed = parseSecureMessageTimestamp(timestamp);

  if (!parsed) {
    return timestamp;
  }

  const today = dayjs();
  const time = formatSecureMessageTime(parsed);

  if (parsed.isSame(today, 'day')) {
    return `Today at ${time}`;
  }

  if (parsed.isSame(today.subtract(1, 'day'), 'day')) {
    return `Yesterday at ${time}`;
  }

  const datePart = parsed.isSame(today, 'year')
    ? parsed.format('ddd, MMM D')
    : parsed.format('ddd, MMM D, YYYY');

  return `${datePart} at ${time}`;
};
